import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import moment from "moment";
import { BondOrderHistoryResponse, getBondOrderSide } from "../Users/Orders/helpers";
import toastr from "toastr";
import {
  downloadPDFFile,
  parseDate,
  replaceCharsForSecurityName,
  toINR,
} from "../../Helpers/functions";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import copy from "copy-to-clipboard";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { downloadUserServiceFileUsingGET } from "../../Api/api";
import axios from "axios";
import BSELogo from "../../images/BSE-Logo_Color.png";

const getColorForDealStatus = (value: string) => {
  switch (value) {
    case "Pending":
      return "warning";
    case "Confirmed":
      return "secondary";
    case "Settled":
      return "success";
    case "Cancelled":
      return "error";
    default:
      return "primary";
  }
};

const normaliseNumericValue = (value?: number | string | null) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getOrderInvestmentAmount = (
  order: Pick<BondOrderHistoryResponse, "total_consideration"> & {
    stamp_duty?: number | string | null;
  }
) => {
  return (
    normaliseNumericValue(order.total_consideration) +
    normaliseNumericValue(order.stamp_duty ?? null)
  );
};

const PartnerClientBondOrderInfo = ({
  item,
  index,
  collapseIndex,
  setCollapseIndex,
  setUUID,
  setOpen,
  user_type,
}: {
  item: BondOrderHistoryResponse;
  index: number;
  collapseIndex: number;
  setCollapseIndex: React.Dispatch<React.SetStateAction<number>>;
  setUUID: React.Dispatch<React.SetStateAction<string>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user_type: string;
}) => {
  const orderSide = useMemo(
    () => getBondOrderSide(item.bid_offer),
    [item.bid_offer]
  );
  const isSellOrder = orderSide === "Sell";

  const downloadQuoteReceipt = (id: string) => {
    const endpoint = isSellOrder
      ? `order_management_v2/client/quote_receipt_download/${id}/?user_type=${user_type}`
      : `order_management_v2/quote_receipt/download/${id}/?type=${user_type}`;

    downloadUserServiceFileUsingGET(endpoint)
      .then((response) => {
        const filePayload = isSellOrder ? response.data : response.data.pdf_file;

        downloadPDFFile(filePayload, `Quote_Receipt_${item.isin}.pdf`)
          .then(() => {
            toastr.success("Quote Receipt Downloaded Successfully.");
          })
          .catch((error) => {
            toastr.error(`Downloaded Failed. - ${error}`);
          });
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          toastr.error(
            `${error.response?.status} : ${JSON.stringify(
              error.response?.data
            )}`
          );
        }
      });
  };

  const downloadOrderReceipt = (id: string) => {
    const endpoint = isSellOrder
      ? `/digio/download_client_order_receipt_pdf/${id}/order/?user_type=${user_type}`
      : `/digio/download_pdf/${id}/order/?type=${user_type}`;

    downloadUserServiceFileUsingGET(endpoint)
      .then((response) => {
        downloadPDFFile(response.data, `Order_Receipt_${item.isin}.pdf`)
          .then(() => {
            toastr.success("Order Receipt Downloaded Successfully.");
          })
          .catch((error) => {
            toastr.error(`Downloaded Failed. - ${error}`);
          });
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          toastr.error(
            `${error.response?.status} : ${JSON.stringify(
              error.response?.data
            )}`
          );
        }
      });
  };

  const downloadDealConfirmation = (id: string) => {
    const endpoint = isSellOrder
      ? `/digio/download_client_order_receipt_pdf/${id}/deal/?user_type=${user_type}`
      : `/digio/download_pdf/${id}/deal/?type=${user_type}`;

    downloadUserServiceFileUsingGET(endpoint)
      .then((response) => {
        downloadPDFFile(response.data, `Dealsheet_${item.isin}.pdf`)
          .then(() => {
            toastr.success("Deal Sheet Downloaded Successfully.");
          })
          .catch((error) => {
            toastr.error(`Downloaded Failed. - ${error}`);
          });
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          toastr.error(
            `${error.response?.status} : ${JSON.stringify(
              error.response?.data
            )}`
          );
        }
      });
  };

  const statuses = useMemo(() => {
    const arr = [
      { title: orderSide, color: "primary.main" },
      {
        title: `Deal ${item.deal_status}`,
        color: `${getColorForDealStatus(item.deal_status)}.main`,
      },
      {
        title: isSellOrder ? "DIS Transfer" : item.payment_process,
        color: "primary.main",
      },
    ];
    if (!isSellOrder && item.payment_process === "PAYMENT GATEWAY") {
      arr.push({
        title: `Payment ${
          ["created", "attempted"].includes(item.payment_status)
            ? "Pending"
            : item.payment_status === "paid"
            ? "Successful"
            : "Pending"
        }`,
        color: item.payment_status === "paid" ? "success.main" : "warning.main",
      });
    }
    return arr;
  }, [item, isSellOrder, orderSide]);

  const basicInfo = useMemo(() => {
    const yieldValue = isSellOrder ? item.offeryield : item.sell_yield;
    const priceValue = isSellOrder ? item.offerprice : item.sell_price;
    const displayDateMoment =
      parseDate(item.last_call_date) ?? parseDate(item.last_maturity_date);

    const formatYield = (value: unknown) => {
      if (value === undefined || value === null || value === "") return "-";
      const stringValue = String(value);
      return stringValue.includes("%") ? stringValue : `${stringValue}%`;
    };

    const formatPrice = (value: unknown) => {
      if (value === undefined || value === null || value === "") return "-";
      const numeric = Number(value);
      return Number.isNaN(numeric) ? String(value) : toINR(numeric, 4);
    };

    return [
      {
        label: "Coupon",
        value: item.security_name?.split(" ")[0],
      },
      {
        label: "Maturity/Call Date",
        value: displayDateMoment
          ? displayDateMoment.format("MMM DD, YYYY")
          : "-",
      },
      {
        label: "Yield",
        value: formatYield(yieldValue),
      },
      {
        label: "Price",
        value: formatPrice(priceValue),
      },
      {
        label: "Settlement Date",
        value: item.settlement_date,
      },
      {
        label: "Settlement Amount",
        value: toINR(getOrderInvestmentAmount(item), 2),
      },
      {
        label: "Quantity",
        value: item.no_of_bonds,
      },
    ];
  }, [isSellOrder, item]);

  const bseStatuses = useMemo(() => {
    return [
      {
        title: "Funds Received",
        value: item.fund_credited === "RECEIVED",
      },
      {
        title: "Securities Received",
        value: item.security_credited === "RECEIVED",
      },
      {
        title: "Funds Given",
        value: item.fund_debited === "RECEIVED",
      },
      {
        title: "Securities Given",
        value: item.security_debited === "RECEIVED",
      },
      {
        title: "Deal Status",
        value: item.dealstatus_settled === "RECEIVED",
      },
    ];
  }, [item]);

  const downloadButtons = useMemo(() => {
    return [
      {
        title: "Quote Receipt",
        onClick: () => downloadQuoteReceipt(item.uuid),
        show: true,
      },
      {
        title: "Order Receipt",
        onClick: () => downloadOrderReceipt(item.rfq_deal_id),
        show: item.is_added_on_rfq,
      },
      {
        title: "Deal Confirmation",
        onClick: () => downloadDealConfirmation(item.rfq_deal_id),
        show: item.deal_status === "Settled",
      },
    ];
  }, [item]);

  const today = moment().endOf("day");
  const maturityDate = moment(item.last_maturity_date);
  const callDate = moment(item.last_call_date);
  const isMatured =
    maturityDate.isSameOrBefore(today) || callDate.isSameOrBefore(today);

  return (
    <Box
      sx={{
        mt: 5,
        borderRadius: "12px",
        border: "1px solid #9397A4",
        p: 2,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          borderRadius: [1, 2],
          overflow: "hidden",
          top: -12,
          left: ["50%", 25],
          transform: ["translateX(-50%)", "translateX(0)"],
          display: "flex",
          alignItems: "stretch",
        }}
      >
        {statuses.map((status, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: status.color,
              display: "flex",
              alignItems: "center",
              py: 0.5,
              px: [1, 2],
            }}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: [7, 10],
                whiteSpace: "nowrap",
              }}
            >
              {status.title}
            </Typography>
          </Box>
        ))}
      </Box>
      <Grid container spacing={[1, 2]} mb={[1, 2]} sx={{ mt: "-5px" }}>
        <Grid item xs={12} sm={true}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #DFE0E4",
                borderRadius: "50%",
                bgcolor: "#FFF",
              }}
            >
              <Box
                component="img"
                src={
                  item.issuer_master_image_s3_url
                    ? item.issuer_master_image_s3_url
                    : "/favicon.png"
                }
                width={40}
                height={40}
                alt="Bond Logo"
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: "contain",
                  borderRadius: "50%",
                  padding: "5px",
                }}
              />
            </Box>
            <Box sx={{ ml: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: [11, 14],
                    fontWeight: 800,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.security_name}
                </Typography>
                <IconButton
                  href={`https://smest.in/bond-details/${item.security_name.replace(
                    /[% ]/g,
                    (m) => replaceCharsForSecurityName[m]
                  )}/${item.isin}`}
                  target="_blank"
                  sx={{ ml: 0.5, pt: 0.5 }}
                >
                  <RocketLaunchRoundedIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: [10, 12],
                    fontWeight: 700,
                    color: "info.main",
                  }}
                >
                  {item.isin}
                </Typography>
                <IconButton
                  sx={{
                    p: 0,
                    ml: 0.5,
                  }}
                  onClick={() => copy(item.isin)}
                >
                  <ContentCopyRoundedIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Grid>
        {item.deal_status !== "Cancelled" && (
          <Grid item xs={12} sm={"auto"} container spacing={1}>
            {downloadButtons.map(
              (button, i) =>
                button.show && (
                  <Grid item xs={"auto"} key={i}>
                    <Button
                      onClick={button.onClick}
                      variant="outlined"
                      sx={{
                        zoom: [0.7, 1],
                        fontWeight: 600,
                        borderRadius: 2,
                        border: "1px solid #53BB53",
                      }}
                      startIcon={<DownloadRoundedIcon />}
                    >
                      {button.title}
                    </Button>
                  </Grid>
                )
            )}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={[0.5, 2]}>
        {basicInfo.map((info, i) => (
          <Grid item xs={6} sm={"auto"} md={1.5} key={i}>
            <Typography
              sx={{
                fontSize: [9, 10],
                fontWeight: 600,
                opacity: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {info.label}
            </Typography>
            <Typography
              sx={{
                fontSize: [10, 13],
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {info.value}
            </Typography>
          </Grid>
        ))}
        {(item.deal_status !== "Settled" || isMatured) && (
          <Grid
            item
            xs={6}
            sm={"auto"}
            md={true}
            container
            alignItems="flex-end"
            justifyContent="flex-end"
          >
            {isMatured ? (
              <Box
                component={"img"}
                src="/images/matured.png"
                alt="Matured"
                width={100}
                height={50}
                style={{
                  objectFit: "contain",
                }}
              />
            ) : (
              <ArrowDownwardRounded
                onClick={() =>
                  collapseIndex === index
                    ? setCollapseIndex(-1)
                    : setCollapseIndex(index)
                }
                style={{
                  transition: "transform 200ms",
                  transform:
                    collapseIndex === index ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
      <Collapse in={collapseIndex === index}>
        <Box
          sx={{
            fontWeight: 600,
            mt: 3,
            mb: 1,
            borderTop: "2px solid #BEC1C9",
            borderBottom: "none !important",
            borderLeft: "none !important",
            borderRight: "none !important",
            borderStyle: "dashed",
            pt: 2,
            display: "flex",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <a href={"https://www.bseindia.com"} target="_blank">
            <Box
              component={"img"}
              src={BSELogo}
              alt="BSE Logo"
              width={50}
              height={30}
              style={{
                marginBottom: -3,
              }}
            />
          </a>
          <Box component={"span"} sx={{ mx: 0.5, opacity: 0.5 }}>
            |
          </Box>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontSize: 13,
            }}
          >
            <Box component="span" sx={{ opacity: 0.5 }}>
              Order ID :
            </Box>
            <Box component="span" sx={{ fontWeight: 800, ml: 0.5 }}>
              {item.rfq_deal_id ? item.rfq_deal_id : "Pending"}
            </Box>
          </Typography>
          <Box component={"span"} sx={{ mx: 0.5, opacity: 0.5 }}>
            |
          </Box>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.7,
            }}
          >
            Exchange Status
          </Typography>
        </Box>
        <Grid container spacing={2} mb={1}>
          {bseStatuses.map((status, i) => (
            <Grid
              item
              xs={6}
              sm={"auto"}
              md={2}
              key={i}
              container
              alignItems={"center"}
              flexWrap="nowrap"
            >
              {status.value ? (
                <CheckCircleTwoToneIcon
                  style={{
                    marginRight: 5,
                    color: "#53BB53",
                  }}
                />
              ) : (
                <CancelTwoToneIcon
                  style={{
                    marginRight: 5,
                    color: "#FF0000",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {status.title}
              </Typography>
            </Grid>
          ))}
          {!isSellOrder &&
            item.deal_status !== "Cancelled" &&
            item.deal_status !== "Settled" &&
            !!!item.razorpay_fe_order_id && (
              <Grid
                item
                xs={6}
                sm={"auto"}
                md={true}
                container
                alignItems="center"
                justifyContent={"flex-end"}
              >
                <Button
                  onClick={() => {
                    setUUID(item.uuid);
                    setOpen(true);
                  }}
                  variant="outlined"
                  sx={{ fontWeight: 600, borderRadius: 2, borderWidth: 1 }}
                  startIcon={<FileUploadRoundedIcon />}
                  disabled={item.isfundtransfer_Uploaded}
                >
                  Fund Proof
                </Button>
              </Grid>
            )}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default PartnerClientBondOrderInfo;
